import React, { useCallback } from "react";
import { Collapse, List, Typography, Button, Switch, Tag, Space, Tooltip, Card, Avatar, Empty, Badge, Divider } from "antd";
import { FileTextOutlined, InfoCircleOutlined, LinkOutlined, PaperClipOutlined, FileAddOutlined, CalendarOutlined, CloudOutlined, PlusOutlined, EyeOutlined, EyeInvisibleOutlined, UnorderedListOutlined, UserOutlined, MailOutlined, TeamOutlined, DeleteOutlined, CheckOutlined } from "@ant-design/icons";

const { Panel } = Collapse;
const { Title, Text, Paragraph } = Typography;

/**
 * V2.5 模式：内容块实例项
 */
const V25InstanceItem = ({ instance, onRemove, onUpdate, onHover }) => {
    const { instanceId, blockId, type, title, data, isInserted, viewMode, displayIndex } = instance;

    // 获取图标
    const getIcon = () => {
        switch (type) {
            case "ScheduleInfo":
                return <InfoCircleOutlined />;
            case "FlightList":
            case "ScheduleFlightList":
                return <CloudOutlined />;
            case "AttachmentList":
                return <PaperClipOutlined />;
            case "ContactList":
                return <UserOutlined />;
            default:
                return <InfoCircleOutlined />;
        }
    };

    // 获取数据预览信息
    const getDataPreview = () => {
        if (!data) {
            return (
                <Text type="secondary" style={{ fontSize: "12px" }}>
                    无数据
                </Text>
            );
        }

        switch (type) {
            case "ScheduleInfo":
                return (
                    <Text type="secondary" style={{ fontSize: "12px" }}>
                        {data.name || "计划信息"}
                    </Text>
                );
            case "FlightList":
            case "ScheduleFlightList":
                if (Array.isArray(data)) {
                    return (
                        <Text type="secondary" style={{ fontSize: "12px" }}>
                            {data.length} 个航班
                        </Text>
                    );
                }
                break;
            case "ContactList":
                if (Array.isArray(data)) {
                    return (
                        <Text type="secondary" style={{ fontSize: "12px" }}>
                            {data.length} 个联系人
                        </Text>
                    );
                }
                break;
            case "AttachmentList":
                if (Array.isArray(data)) {
                    return (
                        <Text type="secondary" style={{ fontSize: "12px" }}>
                            {data.length} 个附件
                        </Text>
                    );
                }
                break;
            default:
                return (
                    <Text type="secondary" style={{ fontSize: "12px" }}>
                        {type} 内容块
                    </Text>
                );
        }

        return (
            <Text type="secondary" style={{ fontSize: "12px" }}>
                有数据
            </Text>
        );
    };

    const handleRemove = () => {
        if (onRemove) {
            onRemove(instanceId);
        }
    };

    const handleViewModeChange = (newViewMode) => {
        if (onUpdate) {
            onUpdate(instanceId, { viewMode: newViewMode });
        }
    };

    const handleMouseEnter = () => {
        if (onHover) {
            onHover(instanceId, true);
        }
    };

    const handleMouseLeave = () => {
        if (onHover) {
            onHover(instanceId, false);
        }
    };

    return (
        <List.Item
            className={`content-block-instance ${isInserted ? "instance-inserted" : "instance-available"}`}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            actions={[
                isInserted ? (
                    <Space key="instance-actions" size="small">
                        <Tooltip title="切换显示模式" key="view-mode">
                            <Button type="text" size="small" icon={viewMode === "summary" ? <EyeOutlined /> : <UnorderedListOutlined />} onClick={() => handleViewModeChange(viewMode === "summary" ? "detailed" : "summary")}>
                                {viewMode === "summary" ? "详细" : "简要"}
                            </Button>
                        </Tooltip>
                        <Tooltip title="从邮件中移除" key="remove">
                            <Button type="text" size="small" icon={<DeleteOutlined />} onClick={handleRemove} danger>
                                移除
                            </Button>
                        </Tooltip>
                    </Space>
                ) : (
                    <Text type="secondary" style={{ fontSize: "12px" }}>
                        已移除
                    </Text>
                ),
            ]}
        >
            <List.Item.Meta
                avatar={
                    <Badge dot={isInserted} offset={[-5, 5]} color={isInserted ? "#52c41a" : "#d9d9d9"}>
                        <Avatar
                            icon={getIcon()}
                            style={{
                                backgroundColor: isInserted ? "#f6ffed" : "#f5f5f5",
                                color: isInserted ? "#52c41a" : "#8c8c8c",
                            }}
                            size="small"
                        />
                    </Badge>
                }
                title={
                    <Space>
                        {/* 显示序号 */}
                        {displayIndex && (
                            <span
                                className="instance-index"
                                style={{
                                    backgroundColor: "#1890ff",
                                    color: "white",
                                    borderRadius: "50%",
                                    width: "18px",
                                    height: "18px",
                                    display: "inline-flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    fontSize: "11px",
                                    fontWeight: "bold",
                                }}
                            >
                                {displayIndex}
                            </span>
                        )}
                        <Text
                            ellipsis
                            style={{
                                opacity: 1,
                                fontWeight: isInserted ? "500" : "normal",
                                color: isInserted ? "#52c41a" : "#8c8c8c",
                            }}
                        >
                            {title}
                        </Text>
                        {isInserted && <CheckOutlined style={{ color: "#52c41a", fontSize: "12px" }} />}
                    </Space>
                }
                description={getDataPreview()}
            />
        </List.Item>
    );
};

/**
 * V2.5 模式：新增内容块按钮
 */
const V25AddBlockButton = ({ block, onInsert, onInsertInline }) => {
    const { id, type, title, data } = block;

    // 获取图标
    const getIcon = () => {
        switch (type) {
            case "ScheduleInfo":
                return <InfoCircleOutlined />;
            case "FlightList":
            case "ScheduleFlightList":
                return <CloudOutlined />;
            case "AttachmentList":
                return <PaperClipOutlined />;
            case "ContactList":
                return <UserOutlined />;
            default:
                return <InfoCircleOutlined />;
        }
    };

    const handleInsert = () => {
        if (onInsert) {
            onInsert(id);
        }
    };

    const handleInsertInline = () => {
        if (onInsertInline) {
            onInsertInline(id);
        }
    };

    return (
        <Card
            size="small"
            className="add-block-card"
            actions={[
                <Tooltip title="插入为块级内容" key="insert-block">
                    <Button type="primary" size="small" icon={<PlusOutlined />} onClick={handleInsert} disabled={!data}>
                        块级
                    </Button>
                </Tooltip>,
                <Tooltip title="插入为行内内容" key="insert-inline">
                    <Button type="default" size="small" icon={<PlusOutlined />} onClick={handleInsertInline} disabled={!data}>
                        行内
                    </Button>
                </Tooltip>,
            ]}
        >
            <Card.Meta
                avatar={
                    <Avatar
                        icon={getIcon()}
                        style={{
                            backgroundColor: "#f0f0f0",
                            color: "#666",
                        }}
                        size="small"
                    />
                }
                title={
                    <Text ellipsis style={{ fontSize: "14px" }}>
                        {title}
                    </Text>
                }
                description={
                    <Text type="secondary" style={{ fontSize: "12px" }}>
                        点击按钮插入新实例
                    </Text>
                }
            />
        </Card>
    );
};

/**
 * V2 模式：内容块控制项（保持原有逻辑，用于V2兼容）
 */
const ContentBlockItem = ({ contentBlock, onVisibilityToggle, onInsertClick }) => {
    const { id, type, displayLabel, visible, config, contextData } = contentBlock;

    // 获取图标
    const getIcon = () => {
        switch (type) {
            case "flightInfo":
            case "flightList":
                return <CloudOutlined style={{ color: config.color }} />;
            case "attachmentList":
            case "attachment":
                return <PaperClipOutlined style={{ color: config.color }} />;
            case "contactList":
            case "contact":
                return <UserOutlined style={{ color: config.color }} />;
            default:
                return <InfoCircleOutlined style={{ color: config.color }} />;
        }
    };

    // 获取数据预览信息
    const getDataPreview = () => {
        if (!contextData) {
            return (
                <Text type="secondary" style={{ fontSize: "12px" }}>
                    无数据
                </Text>
            );
        }

        if (Array.isArray(contextData)) {
            return (
                <Text type="secondary" style={{ fontSize: "12px" }}>
                    {contextData.length} 项数据
                </Text>
            );
        }

        if (typeof contextData === "object") {
            const keys = Object.keys(contextData);
            return (
                <Text type="secondary" style={{ fontSize: "12px" }}>
                    {keys.length} 个字段
                </Text>
            );
        }

        return (
            <Text type="secondary" style={{ fontSize: "12px" }}>
                {String(contextData).substring(0, 20)}...
            </Text>
        );
    };

    const handleVisibilityToggle = (checked) => {
        if (onVisibilityToggle) {
            onVisibilityToggle(id, checked);
        }
    };

    const handleInsert = () => {
        if (onInsertClick) {
            onInsertClick(contentBlock);
        }
    };

    return (
        <List.Item
            className={`content-block-item ${visible ? "item-visible" : "item-hidden"}`}
            actions={[
                <Switch key="visibility" size="small" checked={visible} onChange={handleVisibilityToggle} checkedChildren={<EyeOutlined />} unCheckedChildren={<EyeInvisibleOutlined />} />,
                <Tooltip title="插入到邮件" key="insert">
                    <Button type="text" size="small" icon={<PlusOutlined />} onClick={handleInsert} disabled={!contextData}>
                        插入
                    </Button>
                </Tooltip>,
            ]}
        >
            <List.Item.Meta
                avatar={
                    <Badge dot={!visible} offset={[-5, 5]}>
                        <Avatar
                            icon={getIcon()}
                            style={{
                                backgroundColor: visible ? config.color + "20" : "#f5f5f5",
                                color: visible ? config.color : "#bfbfbf",
                            }}
                            size="small"
                        />
                    </Badge>
                }
                title={
                    <Text
                        ellipsis
                        style={{
                            opacity: visible ? 1 : 0.5,
                            fontWeight: visible ? "normal" : "normal",
                        }}
                    >
                        {displayLabel}
                    </Text>
                }
                description={getDataPreview()}
            />
        </List.Item>
    );
};

/**
 * 邮件编辑器侧边栏组件 - 支持 V1/V2/V2.5 多模式
 * V1: 显示模板中定义的内容块，允许用户控制每个内容块的显示/隐藏
 * V2: 显示可用的内容块，允许用户插入/删除内容块，实现双向绑定
 * V2.5: 支持内容块实例管理，允许多次插入同类型内容块
 */
const SidePanel = ({
    sections = [],
    enableV2Mode = false,
    // V2.5 新增参数
    contentBlockInstances = [],
    onInstanceHover, // 新增：悬浮回调
    // V2 兼容参数
    contentBlockStates = {},
    onInsertBlock,
    onInsertInlineBlock,
    onRemoveBlock,
    onUpdateBlock,
    // V1 兼容参数
    onContentBlockToggle,
    onContentBlockUpdate,
    isTemplateDriven = false,
    onInsert,
    itemStates,
    onStateChange,
}) => {
    // --- Hooks ---
    // 必须在组件顶层调用
    const handleContentBlockVisibilityToggle = useCallback(
        (contentBlockId, visible) => {
            if (onContentBlockToggle) {
                onContentBlockToggle(contentBlockId, visible);
            }
        },
        [onContentBlockToggle],
    );

    const handleContentBlockUpdate = useCallback(
        (contentBlock) => {
            if (onContentBlockUpdate) {
                onContentBlockUpdate(contentBlock);
            }
        },
        [onContentBlockUpdate],
    );

    const handleInsertBlock = useCallback(
        (blockId) => {
            if (onInsertBlock) {
                onInsertBlock(blockId);
            }
        },
        [onInsertBlock],
    );

    const handleInsertInlineBlock = useCallback(
        (blockId) => {
            if (onInsertInlineBlock) {
                onInsertInlineBlock(blockId);
            }
        },
        [onInsertInlineBlock],
    );

    const handleRemoveBlock = useCallback(
        (instanceId) => {
            if (onRemoveBlock) {
                onRemoveBlock(instanceId);
            }
        },
        [onRemoveBlock],
    );

    const handleUpdateBlock = useCallback(
        (instanceId, params) => {
            if (onUpdateBlock) {
                onUpdateBlock(instanceId, params);
            }
        },
        [onUpdateBlock],
    );

    const handleInstanceHover = useCallback(
        (instanceId, isHovering) => {
            if (onInstanceHover) {
                onInstanceHover(instanceId, isHovering);
            }
        },
        [onInstanceHover],
    );

    // --- 日志和提前返回 ---
    if (!sections || sections.length === 0) {
        return (
            <div className="side-panel-empty">
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={enableV2Mode ? "没有可用的内容块" : "模板中没有定义内容块"} style={{ padding: "30px 0" }} />
            </div>
        );
    }

    // 获取section对应的图标
    const getSectionIcon = (sectionId) => {
        switch (sectionId) {
            case "flightInfo":
            case "flightList":
            case "content-blocks":
                return <CloudOutlined />;
            case "attachmentList":
            case "attachment":
                return <PaperClipOutlined />;
            case "contactList":
            case "contact":
                return <UserOutlined />;
            case "flightSchedule":
                return <CalendarOutlined />;
            default:
                return <InfoCircleOutlined />;
        }
    };

    if (enableV2Mode) {
        // V2.5 模式：内容块实例管理
        const collapseItems = sections.map((section) => {
            const { availableBlocks = [], instances = [] } = section;
            const insertedCount = instances.filter((instance) => instance.isInserted).length;

            return {
                key: section.id,
                label: (
                    <div className="section-header">
                        {getSectionIcon(section.id)}
                        <span style={{ marginLeft: 8 }}>{section.title}</span>
                        <Badge
                            count={insertedCount}
                            style={{
                                backgroundColor: insertedCount > 0 ? "#52c41a" : "#d9d9d9",
                                marginLeft: 8,
                            }}
                        />
                    </div>
                ),
                children: (
                    <div className="section-content">
                        {/* 新增区域 */}
                        {availableBlocks.length > 0 && (
                            <div className="add-blocks-section">
                                <Title level={5} style={{ margin: "0 0 12px 0", fontSize: "14px" }}>
                                    <PlusOutlined style={{ marginRight: 4 }} />
                                    新增实例
                                </Title>
                                <div className="add-blocks-grid">
                                    {availableBlocks.map((block) => (
                                        <V25AddBlockButton key={block.id} block={block} onInsert={handleInsertBlock} onInsertInline={handleInsertInlineBlock} />
                                    ))}
                                </div>
                                {instances.length > 0 && <Divider style={{ margin: "16px 0" }} />}
                            </div>
                        )}

                        {/* 已插入实例列表 */}
                        {instances.length > 0 ? (
                            <div className="instances-section">
                                <Title level={5} style={{ margin: "0 0 12px 0", fontSize: "14px" }}>
                                    <UnorderedListOutlined style={{ marginRight: 4 }} />
                                    已插入实例 ({instances.length})
                                </Title>
                                <List itemLayout="horizontal" dataSource={instances} renderItem={(instance) => <V25InstanceItem instance={instance} onRemove={handleRemoveBlock} onUpdate={handleUpdateBlock} onHover={handleInstanceHover} />} />
                            </div>
                        ) : availableBlocks.length === 0 ? (
                            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="该类型没有内容块" style={{ padding: "20px 0" }} />
                        ) : null}
                    </div>
                ),
            };
        });

        return (
            <div className="side-panel-container">
                <div className="side-panel-header">
                    <Title level={5} style={{ margin: 0 }}>
                        <MailOutlined style={{ marginRight: 8 }} />
                        内容块控制 (V2.5)
                    </Title>
                    <Text type="secondary" style={{ fontSize: "12px" }}>
                        管理邮件中的内容块实例
                    </Text>
                </div>

                <Collapse items={collapseItems} defaultActiveKey={sections.map((s) => s.id)} ghost accordion={false} className="side-panel-collapse" />
            </div>
        );
    } else {
        // V1 模式：保持原有逻辑
        const contentBlockSections = sections.filter((section) => section.type === "contentBlocks");

        if (contentBlockSections.length === 0) {
            return (
                <div className="side-panel-empty">
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="没有可用的内容块" style={{ padding: "30px 0" }} />
                </div>
            );
        }

        const collapseItems = contentBlockSections.map((section) => {
            const { contentBlocks = [] } = section;
            const visibleCount = contentBlocks.filter((block) => contentBlockStates[block.id]?.visible !== false).length;

            return {
                key: section.id,
                label: (
                    <div className="section-header">
                        {getSectionIcon(section.id)}
                        <span style={{ marginLeft: 8 }}>{section.title}</span>
                        <Badge
                            count={visibleCount}
                            style={{
                                backgroundColor: "#52c41a",
                                marginLeft: 8,
                            }}
                        />
                    </div>
                ),
                children: (
                    <div className="section-content">
                        {contentBlocks.length > 0 ? (
                            <List
                                itemLayout="horizontal"
                                dataSource={contentBlocks}
                                renderItem={(contentBlock) => (
                                    <ContentBlockItem
                                        contentBlock={{
                                            ...contentBlock,
                                            visible: contentBlockStates[contentBlock.id]?.visible !== false,
                                        }}
                                        onVisibilityToggle={handleContentBlockVisibilityToggle}
                                        onInsertClick={handleContentBlockUpdate}
                                    />
                                )}
                            />
                        ) : (
                            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="该类型没有内容块" style={{ padding: "20px 0" }} />
                        )}
                    </div>
                ),
            };
        });

        return (
            <div className="side-panel-container">
                <div className="side-panel-header">
                    <Title level={5} style={{ margin: 0 }}>
                        <MailOutlined style={{ marginRight: 8 }} />
                        内容块控制
                    </Title>
                    <Text type="secondary" style={{ fontSize: "12px" }}>
                        控制邮件中各个内容块的显示
                    </Text>
                </div>

                <Collapse items={collapseItems} defaultActiveKey={contentBlockSections.map((s) => s.id)} ghost accordion={false} className="side-panel-collapse" />
            </div>
        );
    }
};

export default SidePanel;
